import {
  GLOBALS,
  getUserTimezone,
  handleAggregatorResponseWithHeaders,
  type AggregatedResponse,
} from '@wix/communities-blog-client-common';
import { type CommonThunkAction } from '@app/types/common-thunk-action.type';
import setPosts from '../../../common/actions/set-posts';
import { createPromisifiedAction } from '../../../common/actions-promisifier/create-promisified-action';
import { appendOriginInBackend, appendAuthHeaders } from '../../../common/controller/helpers';
import { getQueryLocale } from '../../../common/selectors/locale-selectors';
import { getTotalResults } from '../../../common/services/pagination';
import { getTimezone } from '../../../common/store/basic-params/basic-params-selectors';
import { setIsLoading } from '../../../common/store/is-loading/is-loading-actions';
import { setPostCount } from '../../../common/store/post-count/set-posts-count';
import { MY_POSTS_PAGE_SIZE, getPostStatusByMyPostsPageTab } from '../../constants/my-posts-page';
import { getActiveTab } from '../active-tab/active-tab-selectors';
import { getViewedUserSiteMemberId } from '../viewed-user/viewed-user-selectors';

export const MY_POSTS_ENTITY = 'my-posts';

export const fetchMyPostsPageInitialData = fetchMyPostsPageRenderModel;
const fetchMyPostsPageData = (page = 1): CommonThunkAction<any> =>
  fetchMyPostsPageRenderModel({ page, fields: ['posts'] });
export const fetchMyPostsPageDataPromisified = createPromisifiedAction(
  fetchMyPostsPageData,
  () => null,
  (response) => response.status,
);

function fetchMyPostsPageRenderModel({
  page,
  fields,
}: { page?: any; fields?: any } = {}): CommonThunkAction<any> {
  return async (dispatch, getState, { httpClient, platformApi, errorHandler }) => {
    const { withErrorHandler } = errorHandler;
    const state = getState();
    const language = getQueryLocale(state);
    const siteMemberId = getViewedUserSiteMemberId(state);
    const activeTab = getActiveTab(state);
    const status = getPostStatusByMyPostsPageTab(activeTab);
    const timezone = getTimezone(state) || getUserTimezone();

    dispatch(setIsLoading(MY_POSTS_ENTITY, undefined, true));

    try {
      const { data } = await withErrorHandler(
        () =>
          httpClient.get<{ posts: AggregatedResponse<unknown> }>(
            appendOriginInBackend({
              platformApi,
              baseUrl: `${GLOBALS.API_FRONTEND_ADAPTER_BASE_URL}/v1/my-posts-page/render-model?${[
                `siteMemberId=${siteMemberId}`,
                `status=${status}`,
                `postLimit=${MY_POSTS_PAGE_SIZE}`,
                `timezone=${timezone}`,
                language && `language=${language}`,
                page && `page=${page}`,
                fields && `fields=${fields.join(',')}`,
              ]
                .filter(Boolean)
                .join('&')}`,
            }),
            {
              headers: appendAuthHeaders(platformApi),
            },
          ),
        { errorCodesMap: {} },
      );
      const { posts } = data;

      const { body, headers } = await handleAggregatorResponseWithHeaders(posts)();
      await dispatch(setPosts(body));
      await dispatch(setPostCount(getTotalResults(headers)));
      dispatch(setIsLoading(MY_POSTS_ENTITY, undefined, false));
    } catch (error) {
      dispatch(setIsLoading(MY_POSTS_ENTITY, undefined, false));
      throw error;
    }
  };
}
